import React, { useEffect, useRef, useState } from "react"
import { AppState, Platform, View } from "react-native"
import { Camera } from "expo-camera"
import {
    getRecordingPermissionsAsync,
    requestRecordingPermissionsAsync,
} from "expo-audio"

export const usePermissions = () => {
    const [permissionsGranted, setPermissionsGranted] = useState({
        microphone: false,
        camera: false,
    })
    const permissionListenersRef = useRef({ mic: null, cam: null, micHandler: null, camHandler: null })

    useEffect(() => {
        let isMounted = true

        const checkPermissions = async () => {
            try {
                if (Platform.OS === "web") {
                    const micPermission = await navigator.permissions.query({ name: "microphone" })
                    const camPermission = await navigator.permissions.query({ name: "camera" })

                    if (!isMounted) return

                    setPermissionsGranted({
                        microphone: micPermission.state === "granted",
                        camera: camPermission.state === "granted",
                    })

                    // Store references for cleanup
                    permissionListenersRef.current.mic = micPermission
                    permissionListenersRef.current.cam = camPermission

                    // Create handlers for cleanup
                    const micHandler = () => {
                        if (isMounted) {
                            setPermissionsGranted(prev => ({
                                ...prev,
                                microphone: micPermission.state === "granted",
                            }))
                        }
                    }
                    const camHandler = () => {
                        if (isMounted) {
                            setPermissionsGranted(prev => ({
                                ...prev,
                                camera: camPermission.state === "granted",
                            }))
                        }
                    }

                    permissionListenersRef.current.micHandler = micHandler
                    permissionListenersRef.current.camHandler = camHandler

                    // Listen for permission changes (e.g., user revokes in browser settings)
                    micPermission.addEventListener("change", micHandler)
                    camPermission.addEventListener("change", camHandler)
                } else {
                    const { status: micStatus } = await getRecordingPermissionsAsync()
                    const { status: camStatus } = await Camera.getCameraPermissionsAsync()

                    if (!isMounted) return

                    setPermissionsGranted({
                        microphone: micStatus === "granted",
                        camera: camStatus === "granted",
                    })
                }
            } catch (error) {
                console.error("Error checking permissions:", error)
                if (isMounted) {
                    setPermissionsGranted({ microphone: false, camera: false })
                }
            }
        }

        checkPermissions()

        // On mobile, re-check permissions when app returns from background
        // (user may have changed permissions in system settings)
        const handleAppState = (nextAppState) => {
            if (nextAppState === "active" && Platform.OS !== "web") {
                checkPermissions()
            }
        }
        const appStateSubscription = AppState.addEventListener("change", handleAppState)

        return () => {
            isMounted = false
            appStateSubscription?.remove()
            // Cleanup permission listeners
            const { mic, cam, micHandler, camHandler } = permissionListenersRef.current
            if (mic && micHandler) {
                mic.removeEventListener("change", micHandler)
            }
            if (cam && camHandler) {
                cam.removeEventListener("change", camHandler)
            }
        }
    }, [])

    const requestPermissions = async () => {
        try {
            if (Platform.OS !== "web") {
                const { status: micRequest } = await requestRecordingPermissionsAsync()
                const { status: camRequest } = await Camera.requestCameraPermissionsAsync()

                setPermissionsGranted({
                    microphone: micRequest === "granted",
                    camera: camRequest === "granted",
                })

            } else {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true })
                    stream.getTracks().forEach((t) => t.stop())
                    setPermissionsGranted({ microphone: true, camera: true })
                } catch (error) {
                    const micPermission = await navigator.permissions.query({ name: "microphone" })
                    const camPermission = await navigator.permissions.query({ name: "camera" })
                    setPermissionsGranted({
                        microphone: micPermission.state === "granted",
                        camera: camPermission.state === "granted",
                    })
                }
            }
        } catch (error) {
            console.error("Error requesting permissions:", error)
        }
    }

    return { permissionsGranted, requestPermissions }
}

const PermissionBanner = () => null

export default PermissionBanner
